import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, Send, Stethoscope } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

const DEAD_END_COPY = "This link has expired. If you need to reschedule, please call us at the clinic.";

export default function PublicAppointmentConfirmation() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || params.token;
  const action = searchParams.get("action");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("details");
  const [details, setDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await supabase.rpc("get_appointment_confirmation_by_token", { p_token: token });
    setLoading(false);
    if (loadError) {
      setError(loadError.message || "Could not load this confirmation link.");
      return;
    }
    setDetails(data);
    if (data?.state === "confirmed" && action !== "reschedule") {
      setView("confirmed");
    } else if (action === "reschedule" && (data?.state === "valid" || data?.state === "confirmed")) {
      const firstDate = (data.available_dates || []).find((day) => day.available)?.date || "";
      setSelectedDate(firstDate);
      setView("reschedule");
    } else {
      setView("details");
    }
  }, [action, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view !== "reschedule" || !selectedDate || !token) return undefined;
    let cancelled = false;
    setSubmitting(true);
    setError(null);
    supabase.rpc("list_appointment_confirmation_slots_for_date", {
      p_token: token,
      p_date: selectedDate,
    }).then(({ data, error: slotError }) => {
      if (cancelled) return;
      if (slotError) {
        setError(slotError.message || "Could not load available times.");
        setSlots([]);
        setSelectedTime("");
        return;
      }
      setSlots(data || []);
      setSelectedTime((data || []).find((slot) => slot.available !== false)?.slot_time || "");
    }).finally(() => {
      if (!cancelled) setSubmitting(false);
    });
    return () => { cancelled = true; };
  }, [selectedDate, token, view]);

  const appointmentTime = useMemo(() => {
    if (!details?.scheduled_start) return "";
    return new Date(details.scheduled_start).toLocaleString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [details?.scheduled_start]);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    const { data, error: confirmError } = await supabase.rpc("confirm_appointment_by_token", { p_token: token });
    setSubmitting(false);
    if (confirmError) {
      setError(confirmError.message || "We could not confirm this appointment.");
      return;
    }
    setDetails(data);
    setView("confirmed");
  }

  async function showReschedule() {
    setError(null);
    const firstDate = (details?.available_dates || []).find((day) => day.available)?.date || "";
    setSelectedDate(firstDate);
    setView("reschedule");
  }

  async function submitReschedule() {
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    setError(null);
    const { data, error: submitError } = await supabase.rpc("request_appointment_reschedule_by_token", {
      p_token: token,
      p_requested_date: selectedDate,
      p_requested_time: selectedTime,
    });
    setSubmitting(false);
    if (submitError) {
      setError(submitError.message || `We could not reschedule this appointment. Please call ${details?.clinic_phone || "the clinic"}.`);
      return;
    }
    setDetails((current) => ({ ...(current || {}), ...(data || {}) }));
    setView("rescheduled");
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading appointment...
        </div>
      </Shell>
    );
  }

  if (error && !details) {
    return <Shell clinicName="Dental OS"><DeadEnd phone={null} message={error} /></Shell>;
  }

  if (!details || details.state === "invalid" || details.state === "expired") {
    return <Shell clinicName={details?.clinic_name}><DeadEnd phone={details?.clinic_phone} /></Shell>;
  }

  return (
    <Shell clinicName={details.clinic_name} logoUrl={details.clinic_logo_url}>
      {view === "confirmed" ? (
        <SuccessPanel title="Appointment confirmed" description={`Your appointment is confirmed. See you on ${appointmentTime}.`} details={details} appointmentTime={appointmentTime} />
      ) : view === "rescheduled" ? (
        <SuccessPanel
          title="Appointment rescheduled"
          description={`Your appointment has been rescheduled to ${appointmentTime}. We'll see you then.`}
          details={details}
          appointmentTime={appointmentTime}
        />
      ) : view === "reschedule" ? (
        <section>
          <HeaderBlock eyebrow="Reschedule" title="Pick a new time" subtitle={`Same doctor: ${details.doctor_name || "your doctor"}`} />
          <div className="mt-5">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Date</p>
            <div className="grid grid-cols-2 gap-1.5">
              {(details.available_dates || []).map((day) => (
                <button
                  key={day.date}
                  type="button"
                  disabled={!day.available}
                  onClick={() => { setSelectedDate(day.date); setError(null); }}
                  className={`min-h-10 rounded-lg border px-2.5 py-2 text-sm font-semibold ${selectedDate === day.date ? "border-teal-500 bg-teal-50 text-teal-950" : day.available ? "border-slate-200 bg-white text-slate-700 hover:border-slate-300" : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300 line-through"}`}
                >
                  {formatDateButton(day.date)}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-2.5">
            {submitting && slots.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading times...</div>
            ) : slots.length > 0 ? (
              <div className="max-h-[52vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {slots.map((slot) => {
                    const available = slot.available !== false;
                    const selected = available && selectedTime === slot.slot_time;
                    return (
                      <button
                        key={slot.slot_time}
                        type="button"
                        disabled={!available}
                        onClick={() => available && setSelectedTime(slot.slot_time)}
                        className={[
                          "min-h-12 rounded-lg border px-2.5 py-2 text-left text-sm transition",
                          selected
                            ? "border-teal-500 bg-teal-50 text-teal-950"
                            : available
                              ? "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                              : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400",
                        ].join(" ")}
                      >
                        <span className="block font-semibold leading-5">{formatSlotTime(slot.slot_time)}</span>
                        {!available ? (
                          <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-400">
                            {slot.unavailable_reason || "Taken"}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-slate-500">No available slots were found. Please call the clinic.</p>
            )}
          </div>
          {error ? <InlineError message={error} phone={details.clinic_phone} /> : null}
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" onClick={submitReschedule} disabled={!selectedDate || !selectedTime || submitting} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Confirm reschedule
            </button>
            <button type="button" onClick={() => setView("details")} className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              Back
            </button>
          </div>
        </section>
      ) : (
        <section>
          <HeaderBlock eyebrow={`Hi ${details.patient_first_name || "there"}`} title={details.clinic_name} subtitle="Please confirm your appointment." />
          <div className="mt-5 space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <InfoRow icon={<CalendarClock className="h-4 w-4" />} label="When" value={appointmentTime} />
            <InfoRow icon={<Stethoscope className="h-4 w-4" />} label="Doctor" value={details.doctor_name || "Your doctor"} />
          </div>
          {error ? <InlineError message={error} phone={details.clinic_phone} /> : null}
          <div className="mt-5 grid gap-2">
            <button type="button" onClick={handleConfirm} disabled={submitting} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-3 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Confirm appointment
            </button>
            <button type="button" onClick={showReschedule} disabled={submitting} className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Reschedule
            </button>
          </div>
          <p className="mt-4 text-center text-xs leading-5 text-slate-500">
            To cancel your appointment, please call us at {details.clinic_phone || "the clinic"}.
          </p>
        </section>
      )}
    </Shell>
  );
}

function Shell({ children, clinicName, logoUrl }) {
  return (
    <main className="min-h-screen bg-slate-50 px-3 py-4 text-slate-900 sm:px-4 sm:py-6">
      <div className="mx-auto max-w-md">
        <div className="mb-3 flex items-center justify-center gap-2 sm:mb-5">
          {logoUrl ? <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-sm font-black text-white">D</span>}
          <span className="min-w-0 truncate text-sm font-bold">{clinicName || "Dental OS"}</span>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">{children}</div>
      </div>
    </main>
  );
}

function HeaderBlock({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center">
      <p className="text-[11px] font-black uppercase tracking-wider text-teal-700">{eyebrow}</p>
      <h1 className="mt-1 text-xl font-bold text-slate-950">{title}</h1>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-teal-700">{icon}</span>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function SuccessPanel({ title, description, details, appointmentTime }) {
  return (
    <section className="text-center">
      <CheckCircle2 className="mx-auto h-11 w-11 text-emerald-500" />
      <h1 className="mt-3 text-xl font-bold text-slate-950">{title}</h1>
      <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
      <div className="mt-5 space-y-3 rounded-xl bg-slate-50 p-4 text-left">
        <InfoRow icon={<CalendarClock className="h-4 w-4" />} label="When" value={appointmentTime} />
        <InfoRow icon={<Stethoscope className="h-4 w-4" />} label="Doctor" value={details.doctor_name || "Your doctor"} />
        {details.clinic_address ? <InfoRow icon={<Stethoscope className="h-4 w-4" />} label="Clinic" value={details.clinic_address} /> : null}
      </div>
    </section>
  );
}

function DeadEnd({ phone, message = DEAD_END_COPY }) {
  const copy = phone ? `This link has expired. If you need to reschedule, please call us at ${phone}.` : message;
  return (
    <section className="py-5 text-center">
      <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
      <h1 className="mt-3 text-lg font-bold text-slate-950">Link unavailable</h1>
      <p className="mt-2 text-sm leading-6 text-slate-600">{copy}</p>
      {phone ? <p className="mt-3 text-base font-bold text-teal-700">{phone}</p> : null}
    </section>
  );
}

function InlineError({ message, phone }) {
  return (
    <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      {message}
      {phone ? <span className="block text-xs text-rose-700">Please call {phone} if this keeps happening.</span> : null}
    </div>
  );
}

function formatSlotTime(value) {
  const [hourRaw, minuteRaw] = String(value || "").split(":");
  const hour24 = Number(hourRaw);
  const minute = Number(minuteRaw || 0);
  if (!Number.isFinite(hour24)) return value;
  const suffix = hour24 >= 12 ? "PM" : "AM";
  const hour = hour24 % 12 || 12;
  return `${hour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatDateButton(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
